import {
  makeStyles,
  shorthands,
  tokens,
  Text,
  Badge,
  Spinner,
} from '@fluentui/react-components';
import { useApi } from '../api/context';
import { useAsync } from '../api/useAsync';
import { TierBadge } from './common';
import { relationshipDescription } from '../theme/pillars';

const useStyles = makeStyles({
  panel: {
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalM),
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalS,
  },
  head: { display: 'flex', alignItems: 'center', columnGap: tokens.spacingHorizontalS },
  explanation: { color: tokens.colorNeutralForeground2, fontSize: tokens.fontSizeBase200 },
  control: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '2px',
    ...shorthands.padding(tokens.spacingVerticalS, 0),
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
  },
  ref: { fontFamily: tokens.fontFamilyMonospace, fontWeight: tokens.fontWeightSemibold },
  muted: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  unlicensed: { color: tokens.colorPaletteDarkOrangeForeground1, fontSize: tokens.fontSizeBase200 },
});

export interface ResolutionPanelProps {
  tenantId: string;
  primitiveId: string | null;
}

/**
 * Shows how a finding maps to the frameworks a customer selected.
 *
 * Three things are shown that most tools flatten away, because flattening them
 * is how a compliance report ends up claiming more than it can support:
 *
 *   the tier, so a control found through the global fallback is not presented
 *   as one the customer asked to be measured against;
 *
 *   the relationship, because "intersects with" is a materially weaker claim
 *   than "equivalent";
 *
 *   a missing control title, which means the framework's content is licensed
 *   and has not been imported, not that the mapping is absent.
 */
export function ResolutionPanel({ tenantId, primitiveId }: ResolutionPanelProps) {
  const styles = useStyles();
  const api = useApi();

  const resolution = useAsync(
    () =>
      primitiveId
        ? api.resolve(tenantId, primitiveId)
        : Promise.resolve(null),
    [tenantId, primitiveId],
  );

  if (!primitiveId) {
    return (
      <div className={styles.panel}>
        <div className={styles.head}>
          <Text weight="semibold" size={200}>Framework mapping</Text>
          <TierBadge tier="unmapped" />
        </div>
        <Text className={styles.explanation}>
          This check is not mapped to a control primitive, so it cannot be expressed
          against any framework. That is a gap in the mapping, not an absence of risk.
        </Text>
      </div>
    );
  }

  if (resolution.loading) {
    return (
      <div className={styles.panel}>
        <Spinner size="tiny" label="Resolving framework mapping" />
      </div>
    );
  }

  if (resolution.error || !resolution.data) {
    return (
      <div className={styles.panel}>
        <Text className={styles.explanation}>
          Could not resolve the framework mapping. {resolution.error ?? ''}
        </Text>
      </div>
    );
  }

  // Defensive: a malformed or unexpected response should degrade to a message,
  // not throw. Without this guard an unexpected shape crashes the panel, React
  // unmounts the tree above it, and the entire findings list disappears with no
  // visible error.
  const tier = resolution.data.tier ?? 'unmapped';
  const explanation = resolution.data.explanation ?? '';
  const controls = Array.isArray(resolution.data.controls) ? resolution.data.controls : [];

  return (
    <div className={styles.panel}>
      <div className={styles.head}>
        <Text weight="semibold" size={200}>Framework mapping</Text>
        <TierBadge tier={tier} />
        <Badge appearance="outline" size="small">{primitiveId}</Badge>
      </div>

      <Text className={styles.explanation}>{explanation}</Text>

      {controls.length === 0 && !explanation && (
        <Text className={styles.explanation}>
          No framework mapping was returned for this control outcome.
        </Text>
      )}

      {controls.map((c) => (
        <div key={`${c.frameworkId}:${c.controlRef}`} className={styles.control}>
          <div>
            <span className={styles.ref}>{c.controlRef}</span>
            <span className={styles.muted}> · {c.frameworkName}</span>
            {c.priority !== null && (
              <span className={styles.muted}> · priority {c.priority}</span>
            )}
          </div>

          {c.controlTitle ? (
            <Text size={200}>{c.controlTitle}</Text>
          ) : (
            // Absent because the framework's content is licensed and has not
            // been imported. The mapping itself is sound.
            <Text className={styles.unlicensed}>
              Control title not loaded. This framework requires a licence; an operator
              imports their own copy.
            </Text>
          )}

          <Text className={styles.muted}>
            {relationshipDescription(c.relationship)} Confidence: {c.confidence}.
          </Text>
        </div>
      ))}
    </div>
  );
}
